import React, { useCallback, useEffect, useState } from 'react';
import noop from 'lodash/noop';
import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Stack } from '@atlaskit/primitives/compiled/stack';

import { cssMap } from '@atlaskit/css';
import { token } from '@atlaskit/tokens';
import Spinner from '@atlaskit/spinner/spinner';
import { fg } from '@atlassian/jira-feature-gating';
import { useQueryParam, useRouterActions } from '@atlassian/react-resource-router';
import type {
	ApplicationLinkManifest,
	ApplicationLinkV3Data,
} from './CommonApplicationLinkCreationEntity.tsx';
import { fetchApplinkManifestForUrl, fetchThisManifest } from './fetchApplinkManifestForUrl.tsx';
import {
	STATUS_RECIPROCAL_INPROGRESS,
	STATUS_RECIPROCAL_SUCCESS,
	parseCreationStateFromUrl,
	type CreationState,
} from './getApplinksAdminUrl.tsx';
import { APPLINKS_LIST_PAGE_URL_LEGACY } from './applinksListPageUrl.constants.tsx';
import ApplinksTable from './ApplinksTable.tsx';
import ApplinksHeader from './ApplinksHeader.tsx';
import { CreateFlowDialog } from './CreateFlowDialog.tsx';
import ReviewSubpage from './ReviewSubpage.tsx';

const styles = cssMap({
	applinksPageContents: {
		maxWidth: '1200px',
		margin: '0 auto',
		paddingTop: token('space.400'),
		paddingRight: token('space.400'),
		paddingBottom: token('space.400'),
		paddingLeft: token('space.400'),
	},
});

type Operation =
	| 'operation-list-applinks'
	| 'operation-oauth1-reciprocal'
	| 'operation-oauth1-complete-link';

const getOperationFromCreationState = (creationState: CreationState | null): Operation => {
	if (!creationState) {
		return 'operation-list-applinks';
	}

	const statusLog = creationState.applinkCreationStatusLog;
	if (statusLog.filter((entry) => entry.status === STATUS_RECIPROCAL_SUCCESS).length > 0) {
		return 'operation-oauth1-complete-link';
	}
	if (statusLog.filter((entry) => entry.status === STATUS_RECIPROCAL_INPROGRESS).length > 0) {
		return 'operation-oauth1-reciprocal';
	}

	return 'operation-list-applinks';
};

const PartialApplinksPageContents = () => {
	const { push } = useRouterActions();

	const [oauth2RemoteApplicationUrl, _] = useQueryParam('oauth2RemoteApplication');
	const [localApplinkManifest, setLocalApplinkManifest] = useState<ApplicationLinkManifest | null>(
		null,
	);
	const [remoteApplinkManifest, setRemoteApplinkManifest] =
		useState<ApplicationLinkManifest | null>(null);

	useEffect(() => {
		if (oauth2RemoteApplicationUrl) {
			fetchApplinkManifestForUrl(oauth2RemoteApplicationUrl).then((remoteManifest) => {
				setRemoteApplinkManifest(remoteManifest);
			});

			fetchThisManifest().then((localManifest) => {
				setLocalApplinkManifest(localManifest);
			});
		}
	}, [oauth2RemoteApplicationUrl]);

	const redirectBackToLegacyListPage = useCallback(() => {
		push(APPLINKS_LIST_PAGE_URL_LEGACY);
	}, [push]);

	useEffect(() => {
		if (!oauth2RemoteApplicationUrl) {
			redirectBackToLegacyListPage();
		}
	}, [oauth2RemoteApplicationUrl, redirectBackToLegacyListPage]);

	return (
		<JSErrorBoundary
			id="applinks-administration-partial-page"
			teamName="olympus"
			packageName="applinksAdministrationPartialPage"
		>
			<Box xcss={styles.applinksPageContents}>
				{localApplinkManifest &&
				remoteApplinkManifest &&
				oauth2RemoteApplicationUrl !== undefined ? (
					<CreateFlowDialog
						isOpen
						onExit={redirectBackToLegacyListPage}
						startingStage={{
							kind: 'review',
							variant: 'OAUTH2.0',
							localManifest: localApplinkManifest,
							remoteManifest: remoteApplinkManifest,
							rpcUrl: oauth2RemoteApplicationUrl,
						}}
					/>
				) : (
					<Spinner interactionName="load" label="Loading" />
				)}
			</Box>
		</JSErrorBoundary>
	);
};

const FullApplinksPageContents = (props: { applinks: ApplicationLinkV3Data[] | null }) => {
	const [createLinkDrawerOpen, setCreateLinkDrawerOpen] = useState<boolean>(false);

	const [creationState] = useState<CreationState | null>(() => {
		// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
		const url = new URL(window.location.href);
		return parseCreationStateFromUrl(url);
	});

	const [operation, setOperation] = useState<Operation>('operation-list-applinks');

	const [thisApplicationManifest, setThisApplicationManifest] =
		useState<ApplicationLinkManifest | null>(null);
	const [targetApplicationManifest, setTargetApplicationManifest] =
		useState<ApplicationLinkManifest | null>(null);

	useEffect(() => {
		const op = getOperationFromCreationState(creationState);
		setOperation(op);
	}, [creationState]);

	useEffect(() => {
		// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
		if (creationState) {
			// We expect to always have an applinkStartingUrl in the creation state for reciprocal review
			// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
			if (operation === 'operation-oauth1-reciprocal' && creationState.applinkStartingUrl) {
				fetchApplinkManifestForUrl(creationState.applinkStartingUrl).then((remoteManifest) => {
					setTargetApplicationManifest(remoteManifest);
				});

				fetchThisManifest().then((localManifest) => {
					setThisApplicationManifest(localManifest);
				});
			}

			// eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
			if (operation === 'operation-oauth1-complete-link') {
				// eslint-disable-next-line react-you-might-not-need-an-effect/no-chain-state-updates
				setCreateLinkDrawerOpen(true);
			}
		}
	}, [creationState, operation]);

	const [filter, setFilter] = useState<string | null>(null);
	const [search, setSearch] = useState<string>('');

	const closeDrawer = () => {
		setCreateLinkDrawerOpen(false);
	};

	return (
		<>
			<JSErrorBoundary
				id="applinks-administration-list-page"
				teamName="olympus"
				packageName="applinksAdministrationListPage"
			>
				<Box xcss={styles.applinksPageContents}>
					{operation === 'operation-list-applinks' && (
						<Stack space="space.400">
							<ApplinksHeader
								onSearch={setSearch}
								onFilterChange={setFilter}
								onCreateLink={() => setCreateLinkDrawerOpen(true)}
								filter={filter}
								search={search}
							/>
							<Stack space="space.800">
								<ApplinksTable applinks={props.applinks} filter={filter} search={search} />
								<CreateFlowDialog isOpen={createLinkDrawerOpen} onExit={closeDrawer} />
							</Stack>
						</Stack>
					)}
					{operation === 'operation-oauth1-reciprocal' && (
						<Stack>
							{(!thisApplicationManifest || !targetApplicationManifest) && (
								<Spinner interactionName="load" label="Loading" />
							)}
							{thisApplicationManifest && targetApplicationManifest && creationState && (
								<ReviewSubpage
									state={{
										kind: 'reciprocal-review',
										variant: 'OAUTH1.0',
										localManifest: thisApplicationManifest,
										remoteManifest: targetApplicationManifest,
										inboundCreationState: creationState,
										rpcUrl: targetApplicationManifest.url,
									}}
									onSuccess={noop}
									onCancel={closeDrawer} // TODO: Reciprocal review should be inside the drawer
									// TODO: Handle error
									onError={noop}
								/>
							)}
						</Stack>
					)}
					{operation === 'operation-oauth1-complete-link' &&
						creationState?.applinkOriginalCreatedId && (
							<CreateFlowDialog
								isOpen={createLinkDrawerOpen}
								onExit={closeDrawer}
								startingStage={{
									kind: 'confirmation',
									variant: 'OAUTH1.0',
									confirmationApplinkId: creationState?.applinkOriginalCreatedId,
								}}
							/>
						)}
				</Box>
			</JSErrorBoundary>
		</>
	);
};

const ApplinksPageContents = (props: {
	applinks: ApplicationLinkV3Data[] | null;
}): React.JSX.Element => {
	if (fg('applinks-933-experimental-list-page')) {
		return <FullApplinksPageContents {...props} />;
	}

	return <PartialApplinksPageContents />;
};

export default ApplinksPageContents;
